import { useId } from 'react';
import type { PropertyCommunicationChannelResponse } from '@/shared/api';
import { InsertFieldMenu, TokenField, useFieldInsertion } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { contentKey, LANGUAGE_LABEL, own, type FieldSpec, type Lang } from './editorModel';
import { fieldIssues } from './fieldIssues';
import { insertOptions } from './insertOptions';
import { segments } from './tokenAnalysis';

interface Props {
  spec: FieldSpec;
  lang: Lang;
  channel: PropertyCommunicationChannelResponse;
  saved?: PropertyCommunicationChannelResponse;
  examples: Record<string, string>;
  hint?: string;
  /** Wechselt, wenn „Zum Feld" genau dieses Feld meint. */
  focusRequest: number;
  onFocused: () => void;
  onChange: (key: ReturnType<typeof contentKey>, value: string | null) => void;
}

/**
 * Ein Feld: angezeigt wird der wirksame Text — der eigene oder der StayOn-Standard. Wer ihn nur
 * ansieht, ändert nichts; erst eine Eingabe macht daraus eine Anpassung, und ein Text gleich dem
 * Standard bleibt Standard.
 */
export function EditorField({ spec, lang, channel, saved, examples, hint, ...props }: Props) {
  const key = contentKey(spec.key, lang);
  const fallback = channel.defaults?.[key] ?? '';
  const value = channel[key] ?? fallback;
  const placeholders = channel.placeholders ?? [];
  const id = useId();
  const rules = { placeholders, field: spec.apiField, max: spec.max, singleLine: spec.singleLine };
  const [issue] = fieldIssues(channel[key], rules);
  const customized = own(channel[key]) !== null;
  const unsaved = own(channel[key]) !== own(saved?.[key]);
  const length = value.trim().length;

  const change = (next: string) => props.onChange(key, next === fallback ? null : next);
  const insertion = useFieldInsertion(value, change, !!spec.apiField);

  return (
    <div className="space-y-1.5">
      <div className="flex min-h-11 flex-wrap items-center justify-between gap-2">
        <label htmlFor={id} className="text-ink text-sm font-semibold">
          {spec.label}
          <span className="sr-only"> ({LANGUAGE_LABEL[lang]})</span>
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-ink-soft inline-flex items-center gap-1.5 text-xs">
            <span
              aria-hidden
              className={cn(
                'size-2 rounded-full',
                unsaved
                  ? 'bg-amber'
                  : customized
                    ? 'bg-postmark'
                    : 'ring-ink-soft ring-[1.5px] ring-inset',
              )}
            />
            {customized ? 'Angepasst' : 'Standard'}
            {unsaved && ' · ungespeichert'}
          </span>
          {customized && (
            <button
              type="button"
              aria-label={`${spec.label} auf Standard zurücksetzen`}
              onClick={() => props.onChange(key, null)}
              className="text-ink focus-visible:outline-sage min-h-11 px-1 text-xs font-semibold underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2"
            >
              Standard
            </button>
          )}
          {spec.apiField && (
            <InsertFieldMenu
              fieldLabel={spec.label}
              options={insertOptions(placeholders, spec.apiField, value, examples)}
              open={insertion.menuOpen}
              onOpenChange={insertion.onMenuOpenChange}
              onInsert={insertion.insert}
              onCloseFocus={insertion.refocus}
            />
          )}
        </div>
      </div>
      <TokenField
        ref={insertion.input}
        id={id}
        value={value}
        placeholder={fallback}
        segments={segments(value, placeholders, spec.apiField)}
        singleLine={spec.singleLine}
        maxLength={spec.max + 200}
        focusRequest={props.focusRequest}
        onFocused={props.onFocused}
        invalid={!!issue}
        describedBy={issue ? `${id}-issue` : hint ? `${id}-hint` : undefined}
        onChange={change}
        onFieldTrigger={insertion.onFieldTrigger}
      />
      {issue && (
        <p id={`${id}-issue`} className="text-sealwax text-xs">
          {issue}
        </p>
      )}
      <div className="text-ink-soft flex flex-wrap justify-between gap-x-3 gap-y-1 text-xs">
        {hint && !issue ? <span id={`${id}-hint`}>{hint}</span> : <span />}
        {(!spec.singleLine || length > spec.max * 0.8) && (
          <span className={cn('tabular-nums', length > spec.max && 'text-sealwax font-semibold')}>
            {spec.max - length < 100 &&
              spec.max >= length &&
              `Noch ${spec.max - length} Zeichen · `}
            {length.toLocaleString('de-DE')} / {spec.max.toLocaleString('de-DE')}
          </span>
        )}
      </div>
    </div>
  );
}
